import React, { useState } from "react";
import { Box, TextField, Divider } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../components/Header";
import { useAlert } from "../../../global/AlertContext";
import { useNavigate } from "react-router-dom";
import { useGetProductsQuery } from "../../../services/productService";
import ProductsList from "../../../components/ProductsList";
import SuccessButton from "../../../components/SuccessButton";
import SearchSelect from "../../../components/SearchSelect";
import CancelButton from "../../../components/CancelButton";
import { useSpendExtraordinaryMutation } from "../../../services/inventoryService";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import UsageList from "../../../components/UsageList";


const CreateUsage = () => {
  const showAlert = useAlert();
  const navigate = useNavigate();
  const [spend] = useSpendExtraordinaryMutation();

  // State for search term
  const [productSearchTerm, setProductSearchTerm] = useState("");

  // Fetch products with search term
  const { data: products } = useGetProductsQuery({
    name: productSearchTerm,
  });

  const [productsToSpend, setProductsToSpend] = useState([]);

  const handleFormSubmit = async (values, { resetForm }) => {
    if (productsToSpend.length > 0) {
      const extractedProducts = productsToSpend.map(product => ({
        product_id: product.product_id,
        quantity: product.quantity,
      }));

      const newUsage = { 
        used_at: values.date.toISOString().replace('T', ' ').replace('Z', '').split('.')[0],
        detail: values.detail, 
        products: extractedProducts 
      };
      try {
        await spend( newUsage ).unwrap();
        showAlert("Salida creada!", "success");
        resetForm();
        navigate("/usages");
      } catch (error) {
        console.error("Error creating usage:", error);
        showAlert(error.message, "error");
      }
    } else {
      showAlert("Debe agregar al menos un producto", "error");
    }
  };

  const initialValues = {
    date: null,
    detail: "",
    product_id: "",
    quantity: 1, 
  };

  const usagesSchema = yup.object().shape({
    date: yup
          .date()
          .typeError("Ingrese una fecha válida")
          .required("Requerido"),
    detail: yup.string().required("Requerido"),
  });

  const handleAddProduct = (values, { resetForm }) => {
    // Validate product and quantity before adding a product
    if (!values.product_id) {
      showAlert("Debe seleccionar un producto", "error");
      return;
    }
    if (values.quantity < 0) {
      showAlert("La cantidad debe ser mayor a 0", "error");
      return;
    }

    // Check if the product is already in the productsToSpend list
    const isProductAlreadyAdded = productsToSpend.some((product) => product.product_id === values.product_id);
    if (isProductAlreadyAdded) {
      showAlert("Este producto ya ha sido agregado", "error");
      return;
    }

    const product = products?.records?.find((p) => p.id === values.product_id);
    if (product) {
      const newProduct = {
        product_id: product.id,
        quantity: values.quantity,
        name: product.name,
        unit_price: product.last_added_cost,
      };
      setProductsToSpend([...productsToSpend, newProduct]);

      // Clear the product field after adding a product
      resetForm({
        values: {
          ...values,
          product_id: "",
          quantity: 1,
        },
      });
      setProductSearchTerm(""); // Clear the search term
    }
  };

  return (
    <Box m="20px">
      <Header title="Salidas" subtitle="Crear Salida Extraordinaria" />
      <Formik
        onSubmit={(values, { resetForm }) => handleFormSubmit(values, { resetForm })}
        initialValues={initialValues}
        validationSchema={usagesSchema}
        validateOnChange={false} // Only validate on submit
        validateOnBlur={false}
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, resetForm }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(6, minmax(0, 1fr))"
            >
              {/* Date Select */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Fecha de Salida"
                  value={values.date ? dayjs(values.date) : null} // Convert to Day.js object
                  onChange={(newValue) => setFieldValue("date", newValue)} // Set as Day.js object
                  format="DD/MM/YYYY" // Set user-friendly format
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                      error: !!touched.date && !!errors.date,
                      helperText: touched.date && errors.date,
                      sx: { gridColumn: "span 3" },
                    },
                  }}
                />
              </LocalizationProvider>
              {/* Details */}
              <TextField
                fullWidth
                variant="filled"
                label="Detalle"
                onChange={handleChange}
                value={values.detail}
                name="detail"
                error={!!touched.detail && !!errors.detail}
                helperText={touched.detail && errors.detail}
                sx={{ gridColumn: "span 3" }}
              />

              {/* Product, Quantity, and Add Button in the same row */}
              <Box sx={{ gridColumn: "span 6", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "30px" }}>
                <SearchSelect
                  label="Producto"
                  options={products?.records || []}
                  value={products?.records?.find((p) => p.id === values.product_id) || null}
                  onChange={(newValue) => {
                    setFieldValue("product_id", newValue?.id || "");
                  }}
                  onInputChange={(newInputValue) => {
                    setProductSearchTerm(newInputValue);
                  }}
                  getOptionLabel={(option) => option.name}
                  error={!!touched.product_id && !!errors.product_id}
                  helperText={touched.product_id && errors.product_id}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Cantidad"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.quantity}
                  name="quantity"
                  inputProps={{ min: 1 }}
                />
                <Box textAlign="center">
                  <SuccessButton
                    label="Agregar Producto"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent the form from submitting
                      handleAddProduct(values, { resetForm });
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: "20px" }} />

            <Box mt="20px">
              <UsageList productsList={productsToSpend} setProductsList={setProductsToSpend} />
            </Box>
            <Box
              display="flex"
              justifyContent="end"
              textAlign="center"
              gap="4px"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/usages')}/>
              <SuccessButton label="Crear Salida" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateUsage;